import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { toast } from 'sonner';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Loader2, Save, Monitor, Smartphone, Tablet, ArrowLeft, RotateCcw, Plus, 
  ChevronUp, ChevronDown, Eye, EyeOff, Trash2, GripVertical, FilePlus, ExternalLink, Undo2, Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { adminButton, adminInput } from '@/components/admin/ui';
import { useAuth } from '@/contexts/AuthContext';
import { useLocalSeoData } from '@/hooks/useLocalSeo';
import { DESCRIPTION_MAX, TITLE_MAX, defaultSeoDescription, defaultSeoTitle, type SeoPage } from '@/lib/localSeo';
import { EditorPreviewFrame } from './EditorPreviewFrame';
import { DeviceMode } from './editor-preview-viewports';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Import page renderers
// Note: renderers are mounted inside the iframe preview for correct responsive breakpoints

// Types
export interface PageData {
  slug: string;
  title: string;
  isBuiltIn: boolean;
  isPublished?: boolean;
  id?: string;
}

type SeoSettings = {
  seo_title: string;
  meta_description: string;
  page_slug: string;
  og_title: string;
  og_description: string;
  social_image_url: string;
  indexing_preference: 'index' | 'noindex';
  canonical_url: string;
};

// Blank title/description = "use the automatic one" (built from the business's
// services and areas — see src/lib/localSeo.ts), shown as the placeholder.
const emptySeo = (slug: string): SeoSettings => ({
  seo_title: '',
  meta_description: '',
  page_slug: slug,
  og_title: '',
  og_description: '',
  social_image_url: '',
  indexing_preference: 'index',
  canonical_url: '',
});

const BUILT_IN_PAGES: PageData[] = [
  { slug: 'home', title: 'Homepage', isBuiltIn: true },
  { slug: 'about', title: 'About Page', isBuiltIn: true },
  { slug: 'services', title: 'Services Page', isBuiltIn: true },
];

interface UnifiedPageEditorProps {
  initialPage?: string;
  onBack?: () => void;
  /** Open the SEO settings dialog straight away (deep link from the SEO check). */
  openSeo?: boolean;
}

const MAX_HISTORY = 50;

const DEVICE_OPTIONS: Array<{ mode: DeviceMode; label: string; Icon: typeof Monitor }> = [
  { mode: 'desktop', label: 'Desktop', Icon: Monitor },
  { mode: 'tablet', label: 'Tablet', Icon: Tablet },
  { mode: 'mobile', label: 'Mobile', Icon: Smartphone },
];

export function UnifiedPageEditor({ initialPage = 'home', onBack, openSeo = false }: UnifiedPageEditorProps) {
  const { orgId, canWrite } = useAdminOrg();
  const { isOrgOwner, isPlatformAdmin } = useAuth();
  const canEdit = canWrite && (isOrgOwner || isPlatformAdmin);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  
  const [selectedPage, setSelectedPage] = useState(initialPage);
  const [customPages, setCustomPages] = useState<PageData[]>([]);
  const [showNewPageDialog, setShowNewPageDialog] = useState(false);
  const [showDeletePageDialog, setShowDeletePageDialog] = useState(false);
  const [showSeoDialog, setShowSeoDialog] = useState(openSeo);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageSlug, setNewPageSlug] = useState('');
  const [seoSettings, setSeoSettings] = useState<SeoSettings>(emptySeo(initialPage));
  const [savingSeo, setSavingSeo] = useState(false);
  const { data: localSeo } = useLocalSeoData(orgId);
  
  // Content state - each page type manages its own content structure
  const [pageContent, setPageContent] = useState<unknown>(null);
  const [originalContent, setOriginalContent] = useState<unknown>(null);

  // Undo history
  const [history, setHistory] = useState<unknown[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoing = useRef(false);

  // Warn on page unload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const fetchCustomPages = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase
      .from('custom_pages')
      .select('id, slug, title, is_published')
      .eq('organization_id', orgId)
      .order('sort_order');

    if (data) {
      setCustomPages(data.map(p => ({
        slug: p.slug,
        title: p.title,
        isBuiltIn: false,
        isPublished: p.is_published,
        id: p.id,
      })));
    }
  }, [orgId]);

  const fetchPageContent = useCallback(async () => {
    setLoading(true);
    
    const builtIn = BUILT_IN_PAGES.find(p => p.slug === selectedPage);
    
    if (builtIn) {
      // Fetch built-in page content from page_content table
      const { data } = await supabase
        .from('page_content')
        .select('content')
        .eq('organization_id', orgId)
        .eq('page', selectedPage)
        .eq('section', 'all')
        .maybeSingle();

      // For homepage, fetch individual sections
      if (selectedPage === 'home') {
        const [heroRes, whyChooseRes, reviewsRes] = await Promise.all([
          supabase.from('page_content').select('content').eq('organization_id', orgId).eq('page', 'homepage').eq('section', 'hero').maybeSingle(),
          supabase.from('page_content').select('content').eq('organization_id', orgId).eq('page', 'homepage').eq('section', 'why_choose').maybeSingle(),
          supabase.from('page_content').select('content').eq('organization_id', orgId).eq('page', 'homepage').eq('section', 'reviews').maybeSingle(),
        ]);
        
        const content = {
          hero: heroRes.data?.content || null,
          whyChoose: whyChooseRes.data?.content || null,
          reviews: reviewsRes.data?.content || null,
        };
        setPageContent(content);
        setOriginalContent(content);
      } else {
        setPageContent(data?.content || null);
        setOriginalContent(data?.content || null);
      }
    } else {
      // Fetch custom page sections
      const customPage = customPages.find(p => p.slug === selectedPage);
      if (customPage?.id) {
        const { data } = await supabase
          .from('page_sections')
          .select('*')
          .eq('page_id', customPage.id)
          .order('sort_order');
        
        setPageContent(data || []);
        setOriginalContent(data || []);
      }
    }

    setLoading(false);
  }, [orgId, selectedPage, customPages]);

  const fetchSeoSettings = useCallback(async () => {
    if (!orgId || !selectedPage) return;

    const fallback = emptySeo(selectedPage);

    const { data } = await supabase
      .from('page_seo_settings')
      .select('seo_title, meta_description, page_slug, og_title, og_description, social_image_url, indexing_preference, canonical_url')
      .eq('organization_id', orgId)
      .eq('page_slug', selectedPage)
      .maybeSingle();

    if (!data) {
      setSeoSettings(fallback);
      return;
    }

    setSeoSettings({
      seo_title: data.seo_title ?? fallback.seo_title,
      meta_description: data.meta_description ?? '',
      page_slug: data.page_slug ?? selectedPage,
      og_title: data.og_title ?? '',
      og_description: data.og_description ?? '',
      social_image_url: data.social_image_url ?? '',
      indexing_preference: data.indexing_preference === 'noindex' ? 'noindex' : 'index',
      canonical_url: data.canonical_url ?? '',
    });
  }, [orgId, selectedPage]);

  // Fetch custom pages on mount (and when the active org changes)
  useEffect(() => {
    fetchCustomPages();
  }, [fetchCustomPages]);

  // Fetch page content when page changes
  useEffect(() => {
    if (selectedPage) {
      fetchPageContent();
      fetchSeoSettings();
    }
  }, [selectedPage, fetchPageContent, fetchSeoSettings]);

  // Track unsaved changes
  useEffect(() => {
    if (originalContent && pageContent) {
      const changed = JSON.stringify(pageContent) !== JSON.stringify(originalContent);
      setHasUnsavedChanges(changed);
    }
  }, [pageContent, originalContent]);

  const saveSeoSettings = async () => {
    if (!canEdit || !orgId) {
      toast.error('Only the business owner can edit SEO settings');
      return;
    }

    setSavingSeo(true);

    try {
      const customPage = customPages.find((page) => page.slug === selectedPage);
      const nextSlug = seoSettings.page_slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const pageSlug = isCustomPage ? nextSlug || selectedPage : selectedPage;

      const payload = {
        organization_id: orgId,
        page_slug: pageSlug,
        custom_page_id: customPage?.id ?? null,
        seo_title: seoSettings.seo_title.trim() || null,
        meta_description: seoSettings.meta_description.trim() || null,
        og_title: seoSettings.og_title.trim() || null,
        og_description: seoSettings.og_description.trim() || null,
        social_image_url: seoSettings.social_image_url.trim() || null,
        indexing_preference: seoSettings.indexing_preference,
        canonical_url: seoSettings.canonical_url.trim() || null,
      };

      const { error } = await supabase
        .from('page_seo_settings')
        .upsert(payload, { onConflict: 'organization_id,page_slug' });

      if (error) throw error;

      if (isCustomPage && customPage?.id && pageSlug !== selectedPage) {
        const { error: pageError } = await supabase
          .from('custom_pages')
          .update({ slug: pageSlug })
          .eq('id', customPage.id)
          .eq('organization_id', orgId);

        if (pageError) throw pageError;
        await fetchCustomPages();
        setSelectedPage(pageSlug);
      }

      setSeoSettings({ ...seoSettings, page_slug: pageSlug });
      setShowSeoDialog(false);
      toast.success('SEO settings saved');
    } catch (error) {
      console.error('SEO save error:', error);
      toast.error('Failed to save SEO settings');
    } finally {
      setSavingSeo(false);
    }
  };

  const saveContent = async () => {
    if (!canEdit) {
      toast.error('Only the business owner can edit pages');
      return;
    }
    setSaving(true);
    
    try {
      const builtIn = BUILT_IN_PAGES.find(p => p.slug === selectedPage);
      
      if (builtIn) {
        if (selectedPage === 'home') {
          // Save homepage sections individually
          const upsertSection = async (section: string, content: Json | null | undefined) => {
            if (!content) return;
            const { data: existing } = await supabase
              .from('page_content')
              .select('id')
              .eq('organization_id', orgId)
              .eq('page', 'homepage')
              .eq('section', section)
              .maybeSingle();

            if (existing) {
              await supabase.from('page_content').update({ content }).eq('id', existing.id);
            } else {
              await supabase.from('page_content').insert({ organization_id: orgId, page: 'homepage', section, content });
            }
          };

          const home = pageContent as { hero?: Json; whyChoose?: Json; reviews?: Json };
          await Promise.all([
            upsertSection('hero', home.hero),
            upsertSection('why_choose', home.whyChoose),
            upsertSection('reviews', home.reviews),
          ]);
        } else {
          // Save about/services as single content blob
          const { data: existing } = await supabase
            .from('page_content')
            .select('id')
            .eq('organization_id', orgId)
            .eq('page', selectedPage)
            .eq('section', 'all')
            .maybeSingle();

          if (existing) {
            await supabase.from('page_content').update({ content: pageContent as Json }).eq('id', existing.id);
          } else {
            await supabase.from('page_content').insert({ organization_id: orgId, page: selectedPage, section: 'all', content: pageContent as Json });
          }
        }
      } else {
        // Save custom page sections
        const sections = pageContent as Array<{
          id: string;
          content: Json;
          layout_variant: string;
          is_visible: boolean;
          sort_order: number;
        }>;
        for (const section of sections) {
          await supabase
            .from('page_sections')
            .update({
              content: section.content,
              layout_variant: section.layout_variant,
              is_visible: section.is_visible,
              sort_order: section.sort_order,
            })
            .eq('id', section.id);
        }
      }

      setOriginalContent(pageContent);
      setHasUnsavedChanges(false);
      toast.success('Changes saved!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (newPage: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => setSelectedPage(newPage));
      setShowUnsavedDialog(true);
    } else {
      setSelectedPage(newPage);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => onBack);
      setShowUnsavedDialog(true);
    } else {
      onBack?.();
    }
  };

  const handleDiscardAndNavigate = () => {
    setShowUnsavedDialog(false);
    setHasUnsavedChanges(false);
    pendingNavigation?.();
    setPendingNavigation(null);
  };

  const handleSaveAndNavigate = async () => {
    await saveContent();
    setShowUnsavedDialog(false);
    pendingNavigation?.();
    setPendingNavigation(null);
  };

  const resetChanges = () => {
    if (originalContent) {
      isUndoing.current = true;
      setPageContent(JSON.parse(JSON.stringify(originalContent)));
      setHasUnsavedChanges(false);
      setHistory([]);
      setHistoryIndex(-1);
      setTimeout(() => { isUndoing.current = false; }, 0);
    }
  };

  const undo = () => {
    if (historyIndex > 0) {
      isUndoing.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setPageContent(JSON.parse(JSON.stringify(history[newIndex])));
      setTimeout(() => { isUndoing.current = false; }, 0);
    } else if (historyIndex === 0 && originalContent) {
      isUndoing.current = true;
      setHistoryIndex(-1);
      setPageContent(JSON.parse(JSON.stringify(originalContent)));
      setTimeout(() => { isUndoing.current = false; }, 0);
    }
  };

  const canUndo = historyIndex >= 0;

  const createCustomPage = async () => {
    if (!canEdit) {
      toast.error('Only the business owner can create pages');
      return;
    }
    if (!newPageTitle || !newPageSlug) {
      toast.error('Please fill in all fields');
      return;
    }

    const slug = newPageSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-');

    const { data, error } = await supabase
      .from('custom_pages')
      .insert([{ organization_id: orgId, title: newPageTitle, slug, is_published: false }])
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        toast.error('A page with this URL already exists');
      } else {
        toast.error('Failed to create page');
      }
    } else {
      await fetchCustomPages();
      setSelectedPage(slug);
      setShowNewPageDialog(false);
      setNewPageTitle('');
      setNewPageSlug('');
      toast.success('Page created');
    }
  };

  const deleteCustomPage = async () => {
    if (!canEdit) {
      toast.error('Only the business owner can delete pages');
      return;
    }
    const customPage = customPages.find(p => p.slug === selectedPage);
    if (!customPage?.id) return;

    await supabase.from('page_sections').delete().eq('page_id', customPage.id);
    const { error } = await supabase.from('custom_pages').delete().eq('id', customPage.id);

    if (error) {
      toast.error('Failed to delete page');
    } else {
      await fetchCustomPages();
      setSelectedPage('home');
      setShowDeletePageDialog(false);
      toast.success('Page deleted');
    }
  };

  const updateContent = useCallback((newContent: unknown) => {
    if (!canEdit) return;
    // Add to undo history when content changes (but not during undo/reset)
    if (!isUndoing.current && pageContent !== null) {
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(JSON.parse(JSON.stringify(pageContent)));
        // Limit history size
        if (newHistory.length > MAX_HISTORY) {
          newHistory.shift();
        }
        return newHistory;
      });
      setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
    }
    setPageContent(newContent);
  }, [canEdit, pageContent, historyIndex]);

  const allPages = [...BUILT_IN_PAGES, ...customPages];
  const currentPage = allPages.find(p => p.slug === selectedPage);
  const isCustomPage = !BUILT_IN_PAGES.find(p => p.slug === selectedPage);
  const autoSeoPage: SeoPage = isCustomPage
    ? { kind: 'custom', slug: selectedPage, title: customPages.find((p) => p.slug === selectedPage)?.title ?? selectedPage }
    : { kind: selectedPage as 'home' | 'services' | 'about' };
  const autoSeoCtx = {
    businessName: localSeo?.company?.company_name?.trim() || '',
    company: localSeo?.company,
    services: localSeo?.services,
  };
  const autoSeo = localSeo
    ? { title: defaultSeoTitle(autoSeoPage, autoSeoCtx), description: defaultSeoDescription(autoSeoPage, autoSeoCtx) }
    : { title: '', description: '' };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] md:h-screen bg-background">
      {/* Top toolbar */}
      <div className="adm-topbar sticky top-0 z-50 px-2.5 py-2 sm:px-4">
        <div className="flex items-center justify-between gap-2">
          {/* Left side - Navigation & Page selector */}
          <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={handleBack} className={cn(adminButton.ghost, 'shrink-0 px-2 sm:px-2.5')}>
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
            )}
            
            <div className="h-6 w-px bg-border hidden sm:block shrink-0" />
            
            {/* Page selector */}
            <Select value={selectedPage} onValueChange={handlePageChange}>
              <SelectTrigger aria-label="Page" className={cn(adminInput, 'w-full min-w-[72px] max-w-[190px] font-medium')}>
                <SelectValue placeholder="Select page" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Pages</div>
                {BUILT_IN_PAGES.map((page) => (
                  <SelectItem key={page.slug} value={page.slug}>
                    {page.title}
                  </SelectItem>
                ))}
                {customPages.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground mt-2">Custom</div>
                    {customPages.map((page) => (
                      <SelectItem key={page.slug} value={page.slug}>
                        {page.title}
                        {!page.isPublished && <span className="ml-2 text-xs opacity-60">(Draft)</span>}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>

            {hasUnsavedChanges && (
              <span className="adm-chip adm-chip--amber adm-chip--dot hidden sm:inline-flex">
                Unsaved
              </span>
            )}
            {!canEdit && (
              <span className="adm-chip adm-chip--grey hidden sm:inline-flex">
                Read-only
              </span>
            )}
          </div>

          {/* Right side - Controls */}
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {/* Device toggle */}
            <div className="adm-segmented shrink-0" role="group" aria-label="Preview size">
              {DEVICE_OPTIONS.map(({ mode, label, Icon }) => (
                <button
                  key={mode}
                  type="button"
                  aria-label={`${label} preview`}
                  aria-pressed={deviceMode === mode}
                  title={`${label} preview`}
                  onClick={() => setDeviceMode(mode)}
                  className={cn(
                    'inline-flex h-7 w-7 items-center sm:w-8 justify-center rounded-[7px] text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    deviceMode === mode && 'bg-card text-foreground shadow-[var(--adm-shadow-sm)]',
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>

            {/* Actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className={cn(adminButton.outline, 'px-2.5 sm:px-3')}>
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={!canEdit} onClick={() => setShowNewPageDialog(true)}>
                  <FilePlus className="h-4 w-4 mr-2" />
                  New Page
                </DropdownMenuItem>
                <DropdownMenuItem disabled={!canEdit} onClick={() => setShowSeoDialog(true)}>
                  <Search className="h-4 w-4 mr-2" />
                  SEO Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open(`/${selectedPage === 'home' ? '' : selectedPage}`, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Preview Live
                </DropdownMenuItem>
                {isCustomPage && (
                  <DropdownMenuItem 
                    className="text-destructive focus:text-destructive"
                    disabled={!canEdit}
                    onClick={() => setShowDeletePageDialog(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Page
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Undo button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={undo} 
              disabled={!canUndo || !canEdit}
              className={cn(adminButton.outline, 'px-2.5 sm:px-3')}
              title="Undo last change"
            >
              <Undo2 className="h-4 w-4" />
              <span className="hidden sm:inline">Undo</span>
            </Button>

            {/* Reset button */}
            {hasUnsavedChanges && (
              <Button variant="outline" size="sm" onClick={resetChanges} disabled={!canEdit} className={cn(adminButton.outline, 'px-2.5 sm:px-3')}>
                <RotateCcw className="h-4 w-4" />
                <span className="hidden sm:inline">Reset</span>
              </Button>
            )}

            {/* Save button */}
            <Button size="sm" onClick={saveContent} disabled={saving || !hasUnsavedChanges || !canEdit} className={cn(adminButton.primary, 'px-2.5 sm:px-3.5')}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Preview area */}
      <EditorPreviewFrame
        deviceMode={deviceMode}
        page={selectedPage}
        pageId={isCustomPage ? customPages.find(p => p.slug === selectedPage)?.id : null}
        content={pageContent}
        onContentChange={updateContent}
      />

      {/* Unsaved changes dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them before leaving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button variant="outline" size="sm" onClick={handleDiscardAndNavigate} className={adminButton.outline}>Discard</Button>
            <AlertDialogAction onClick={handleSaveAndNavigate} disabled={!canEdit}>Save & Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New page dialog */}
      <Dialog open={showNewPageDialog} onOpenChange={setShowNewPageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Page</DialogTitle>
            <DialogDescription>Add a new custom page to your website</DialogDescription>
          </DialogHeader>
          <div className="adm-form space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Page Title</Label>
              <Input
                value={newPageTitle}
                onChange={(e) => {
                  setNewPageTitle(e.target.value);
                  setNewPageSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'));
                }}
                placeholder="e.g. Our Team"
              />
            </div>
            <div className="space-y-2">
              <Label>URL Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm">/</span>
                <Input
                  value={newPageSlug}
                  onChange={(e) => setNewPageSlug(e.target.value)}
                  placeholder="our-team"
                />
              </div>
            </div>
            <Button size="sm" onClick={createCustomPage} disabled={!canEdit} className={cn(adminButton.primary, 'w-full')}>Create Page</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SEO settings dialog */}
      <Dialog open={showSeoDialog} onOpenChange={setShowSeoDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>SEO Settings</DialogTitle>
            <DialogDescription>Set the search and social sharing details for {currentPage?.title ?? selectedPage}.</DialogDescription>
          </DialogHeader>
          <div className="adm-form space-y-4 pt-2">
            <div className="space-y-2">
              <Label>SEO Page Title</Label>
              <Input
                value={seoSettings.seo_title}
                onChange={(e) => setSeoSettings({ ...seoSettings, seo_title: e.target.value })}
                placeholder={autoSeo.title || currentPage?.title || 'Page title'}
              />
              <p className="text-xs text-muted-foreground">
                {seoSettings.seo_title.length}/{TITLE_MAX} characters · leave blank to use the automatic title shown
              </p>
            </div>
            <div className="space-y-2">
              <Label>Meta Description</Label>
              <Textarea
                value={seoSettings.meta_description}
                onChange={(e) => setSeoSettings({ ...seoSettings, meta_description: e.target.value })}
                placeholder={autoSeo.description || 'A short summary shown in search results.'}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                {seoSettings.meta_description.length}/{DESCRIPTION_MAX} characters · leave blank to use the automatic description shown
              </p>
            </div>
            <div className="space-y-2">
              <Label>URL Slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/</span>
                <Input
                  value={isCustomPage ? seoSettings.page_slug : selectedPage}
                  onChange={(e) => setSeoSettings({ ...seoSettings, page_slug: e.target.value })}
                  disabled={!isCustomPage}
                />
              </div>
              {!isCustomPage && <p className="text-xs text-muted-foreground">Built-in page URLs are fixed for now.</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Open Graph Title</Label>
                <Input
                  value={seoSettings.og_title}
                  onChange={(e) => setSeoSettings({ ...seoSettings, og_title: e.target.value })}
                  placeholder="Falls back to SEO title"
                />
              </div>
              <div className="space-y-2">
                <Label>Social Sharing Image URL</Label>
                <Input
                  value={seoSettings.social_image_url}
                  onChange={(e) => setSeoSettings({ ...seoSettings, social_image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Open Graph Description</Label>
              <Textarea
                value={seoSettings.og_description}
                onChange={(e) => setSeoSettings({ ...seoSettings, og_description: e.target.value })}
                placeholder="Falls back to meta description"
                rows={2}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Indexing</Label>
                <Select
                  value={seoSettings.indexing_preference}
                  onValueChange={(value) => setSeoSettings({ ...seoSettings, indexing_preference: value as 'index' | 'noindex' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="index">Allow indexing</SelectItem>
                    <SelectItem value="noindex">Hide from search engines</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Canonical URL</Label>
                <Input
                  value={seoSettings.canonical_url}
                  onChange={(e) => setSeoSettings({ ...seoSettings, canonical_url: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>
            <Button size="sm" onClick={saveSeoSettings} disabled={!canEdit || savingSeo} className={cn(adminButton.primary, 'w-full')}>
              {savingSeo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save SEO Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete page dialog */}
      <AlertDialog open={showDeletePageDialog} onOpenChange={setShowDeletePageDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete page?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{currentPage?.title}" and all its content. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCustomPage} disabled={!canEdit} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
